export default {
  layout: {
    tabEvents: "Economic events",
    eventsDescription: "Economic events and ledger allocation snapshots",
    tabExternal: "External payments",
    externalDescription: "External PSP payments",
    tabSettlements: "Settlements",
    settlementsDescription: "Open settlement lines by payee",
  },
  shared: {
    emptyCell: "—",
    colCreated: "Created",
    colType: "Type",
    colStatus: "Status",
    colAmount: "Amount",
  },
  eventsList: {
    loadError: "Could not load economic events",
    colGross: "Gross",
    colMethod: "Method",
    colLines: "Lines",
    colExternalPayment: "Payment",
    openRowAria: "Open event {{id}}",
    eventType: {
      CHECKOUT_CAPTURED: "Checkout",
      BOOKING_PAYMENT_CAPTURED: "Booking payment",
      SELLER_FEE_FROM_RECEIPT: "Receipt fee",
      REFUND_REVERSAL: "Refund reversal",
      SELLER_BANK_TRANSFER_SENT: "Bank transfer sent",
      SELLER_BANK_TRANSFER_RECEIVED: "Bank transfer received",
    },
    eventStatus: {
      DRAFT: "Draft",
      POSTED: "Posted",
      REVERSED: "Reversed",
    },
    allocationMethod: {
      USER_PAYMENT: "User payment",
      SELLER_FEE: "Seller fee",
      REFUND: "Refund",
      TREASURY_SETTLEMENT: "Treasury settlement",
    },
  },
  externalList: {
    statusAll: "All statuses",
    openRowAria: "Show payment {{id}}",
    loadError: "Could not load external payments",
    statusCountsLoadError: "Could not load the status counts",
    linkedPayment: "Linked payment",
    clearHighlight: "Clear highlight",
    selectedPayment: "Selected payment",
    providerFlow: "Provider / flow",
    providerPaymentId: "Provider payment id",
    idempotencyKey: "Idempotency key",
    booking: "Booking",
    paymentId: "Payment id",
    colProviderRef: "Provider ref",
    colBooking: "Booking",
    colPaymentId: "Payment id",
    paymentStatus: {
      CREATED: "Created",
      AUTHORIZED: "Authorized",
      CAPTURED: "Captured",
      FAILED: "Failed",
      CANCELED: "Canceled",
      REFUNDED: "Refunded",
      REFUND_FAILED: "Refund failed",
      PENDING: "Pending",
      AWAITING_APP: "Awaiting app",
      PENDING_RECONCILIATION: "Pending reconciliation",
      COMPLETED: "Completed",
      REVERTED: "Reverted",
    },
    paymentType: {
      CHECKOUT_PAYMENT: "Checkout payment",
      SELLER_PAYOUT: "Seller payout",
    },
    provider: {
      SUMUP: "SumUp",
      MOCK: "Mock",
      REVOLUT: "Revolut",
    },
    flow: {
      CLOUD_POS: "Cloud POS",
      BANK_TRANSFER: "Bank transfer",
    },
  },
  eventDetail: {
    splitAria: "Allocation split by role",
    technicalTitle: "Technical data",
    technicalHint: "Allocation rule snapshot and raw metadata",
    roles: {
      PROCESSOR_FEES: "Processor fees",
      SELLER: "Seller",
      HOST: "Host",
      SALES_AGENT_ACTIVITY: "Sales agent (activity)",
      SALES_AGENT_HOUSE: "Sales agent (house)",
      PLATFORM: "Platform",
      TREASURY_CLEARING: "Treasury clearing",
    },
    loadError: "Could not load event",
    loading: "Loading event…",
    notFound: "Event not found.",
    backToEvents: "Back to events",
    allEvents: "All events",
    viewExternalPayment: "View payment",
    cardTitle: "Economic event",
    currency: "Currency",
    grossAmount: "Gross amount",
    idempotencyKey: "Idempotency key",
    ledgerEntries: "Ledger entries",
    bookingId: "Booking id",
    checkoutId: "Checkout id",
    reversesEvent: "Reverses event",
    updated: "Updated",
    jsonAllocationSnapshot: "Allocation rule snapshot",
    jsonMetadata: "Metadata",
    jsonNone: "None",
    allocationLinesTitle: "Allocation lines",
    allocationLinesSubtitle:
      "Frozen snapshot posted to the ledger ({{count}} lines)",
    colStep: "Step",
    colRole: "Role",
    colStoredPercent: "Stored %",
    colAbsolutePercent: "Absolute %",
    colLedgerAccount: "Ledger account",
    colLineId: "Line id",
    noAllocationLines: "No allocation lines",
  },
  settlements: {
    roles: {
      seller: "Seller",
      host: "Host",
      salesAgent: "Sales agent",
    },
    payees: {
      kpiPayeeCount_one: "{{count}} payee",
      kpiPayeeCount_other: "{{count}} payees",
      loadError: "Could not load settlement payees",
      searchPlaceholder: "Name or email",
      filterRole: "Role",
      filterAllRoles: "All roles",
      filterDefault: "Open",
      filterToBePaid: "To be paid",
      filterToBeInvoiced: "To be invoiced",
      colName: "Payee",
      colRole: "Role",
      colPayable: "Open payable",
      colCommissionOpen: "Open commission",
      colSupplierOpen: "Awaiting supplier inv.",
      legalIncompleteTooltip: "Legal info not complete",
      openRowAria: "Open payee {{name}}",
      amountWithCount: "{{amount}} ({{count}})",
      empty: "No payees found",
    },
    detail: {
      loadError: "Could not load settlement lines",
      loading: "Loading settlement lines…",
      back: "Back to payees",
      linesTitle: "Settlement lines",
      linesSubtitle:
        "Select the lines that drive the workflow actions on the right.",
      flowFilter: "Flow",
      paymentFilter: "Payment",
      invoiceFilter: "Invoice",
      flowFilterAll: "All",
      flowFilterUpfront: "Upfront activity",
      flowFilterReceiptBased: "Receipt-based activity",
      flowFilterOutgoingCommissions: "Outgoing commissions",
      paymentFilterAll: "All",
      paymentFilterUnpaid: "Unpaid",
      paymentFilterPaid: "Paid",
      paymentFilterUncollected: "Uncollected",
      paymentFilterCollected: "Collected",
      invoiceFilterAll: "All",
      invoiceFilterUninvoiced: "Uninvoiced",
      invoiceFilterInvoiced: "Invoiced",
      invoiceFilterPending: "Pending",
      invoiceFilterReceived: "Received",
      deselectAll: "Deselect all",
      emptyFiltered: "No lines match the current filters",
      filteredCount: "Showing {{shown}} of {{total}} lines",
      invoiceToInvoice: "To invoice",
      invoiceInvoiced: "Invoiced",
      invoiceSummaryNotSent: "Summary not sent",
      invoiceAwaitingInvoice: "Awaiting invoice",
      invoiceReceived: "Received invoice",
      paymentAwaiting: "Awaiting",
      paymentReceived: "Received",
      paymentToPay: "To pay",
      paymentPaid: "Paid",
      paymentNotAvailable: "Not available",
      copyCollectionReference: "Copy collection reference",
      copyInvoiceReference: "Copy invoice reference",
      copyPaymentReference: "Copy payment reference",
      actionsPanelTitle: "Actions",
      actionsPanelSubtitle: "Run settlement steps for the selected lines.",
      selectionSummaryTitle: "Selection summary",
      readOnlyHint: "You have read-only access to this settlement.",
      finalizedReadOnlyHint:
        "This settlement is invoiced and paid, so fields are read-only.",
      noActionsHint: "Select lines to see available actions.",
      selectMetricPayable: "Select open payable lines",
      selectMetricInvoiceable: "Select open commission lines",
      selectMetricCollectible: "Select lines to collect",
      selectMetricSupplierInvoice: "Select lines awaiting supplier invoice",
      openPayable: "Open payable",
      openInvoiceable: "Open commission to invoice",
      openPendingSupplierInvoice: "Awaiting supplier invoice",
      openCollectible: "Open to collect",
      selectAll: "Select all",
      selectLine: "Select line for {{activity}}",
      colEvent: "Event",
      colInvoiceAmount: "Invoice amount",
      colInvoiceStatus: "Invoice status",
      moneyDirectionOutgoing: "Outgoing money",
      moneyDirectionIncoming: "Incoming money",
      colPaymentAmount: "Payment amount",
      colPaymentStatus: "Payment status",
      viewFePdf: "View",
      downloadFePdf: "Download",
      downloadFePdfError: "Could not download the commission invoice PDF",
      empty: "No settlement lines",
      selectedPayable: "Selected payable",
      selectedInvoiceable: "Selected invoiceable",
      selectedCount: "Selected lines",
      markInvoiced: "Mark invoiced",
      markPaid: "Mark paid",
      invoiceNotePlaceholder: "Note for commission invoice (optional)",
      collectionNotePlaceholder: "Note for collection (optional)",
      supplierNotePlaceholder: "Note for supplier invoice (optional)",
      paymentNotePlaceholder: "Note for payout (optional)",
      referencePlaceholder: "Bank transfer reference",
      referenceRequired: "Payment reference is required",
      invoicedSuccess: "Selected lines marked as invoiced",
      invoicedError: "Could not mark lines as invoiced",
      paidSuccess: "Selected lines marked as paid",
      paidError: "Could not mark lines as paid",
      selectedCollectible: "Selected to collect",
      viewSupplierInvoice: "View invoice",
      downloadSupplierInvoice: "Download invoice",
      downloadSupplierInvoiceError: "Could not download the supplier invoice",
      flowLabel: {
        UPFRONT: "Upfront payment",
        RECEIPT_BASED: "Receipt-based payment",
        SUPPLIER_PAYOUT: "Supplier payout",
      },
      flowHint: {
        UPFRONT:
          "Money already collected: issue the commission invoice and pay the seller.",
        RECEIPT_BASED:
          "Send the pro forma, record the bank transfer, then issue the invoice.",
        SUPPLIER_PAYOUT:
          "Send the summary, record the received invoice, then pay the seller.",
      },
      mixedSelectionWarning:
        "Selected lines follow different settlement workflows. Narrow the selection to one workflow before running actions.",
      emailNever: "Never sent",
      emailLast: "Last sent {{when}}",
      emailSentCount: "Sent {{count}}×",
      steps: {
        summaryTitle: "Send summary",
        summaryDesc:
          "Email the seller the summary of the selected transactions (PDF attached).",
        summaryButton: "Send summary email",
        summaryResend: "Resend summary",
        proformaTitle: "Send pro forma",
        proformaDesc:
          "Email the seller the pro forma for the selected commission lines before recording collection.",
        proformaButton: "Send pro forma email",
        proformaResend: "Resend pro forma",
        summarySuccess: "Summary email sent",
        summaryError: "Could not send the summary email",
        summaryAllInvoiced: "All selected lines are already invoiced",
        summarySkipped: "Email not sent: {{detail}}",
        invoiceTitle: "Issue invoice",
        invoiceDesc: "Issue the commission invoice for the selected lines.",
        collectTitle: "Record collection",
        collectDesc:
          "Record the payment received from the seller for the selected lines.",
        collectButton: "Record collection",
        collectSuccess: "Collection recorded",
        collectError: "Could not record the collection",
        collectReferencePlaceholder: "Incoming transfer reference",
        supplierTitle: "Record received invoice",
        supplierDesc:
          "Upload the invoice issued by the seller (one PDF can cover several lines).",
        supplierRegisteredDesc:
          "This selection already has a registered supplier invoice.",
        supplierRegisteredUntitled: "Registered invoice",
        supplierRegisteredAt: "Registered {{when}}",
        supplierReplaceDesc: "Upload a new file to replace the registered invoice.",
        supplierReplaceButton: "Replace invoice",
        supplierReplaceSubmit: "Save replacement",
        supplierReplaceSuccess: "Supplier invoice replaced",
        supplierReplaceError: "Could not replace the supplier invoice",
        supplierButton: "Record received invoice",
        supplierNumberLabel: "Invoice number",
        supplierNumberPlaceholder: "e.g. 2026/123",
        supplierDateLabel: "Invoice date (optional)",
        supplierTotalLabel: "Total €",
        supplierExpectedTotal: "Must match selected lines: {{amount}}",
        supplierFileLabel: "Invoice file (PDF or image)",
        supplierNumberRequired: "Enter the invoice number",
        supplierFileRequired: "Select the invoice file",
        supplierFileTooLarge: "File is too large. Maximum size is {{max}}.",
        supplierFileUnsupported: "Please upload an image or PDF invoice.",
        supplierTotalRequired: "Enter the invoice total",
        supplierTotalInvalid: "Invalid amount (e.g. 1,234.56 or 1.234,56)",
        supplierTotalMismatch:
          "Invoice total must match the selected lines ({{expected}}).",
        supplierSuccess: "Supplier invoice recorded",
        supplierError: "Could not record the supplier invoice",
        payTitle: "Issue payment",
        payDesc: "Pay the seller via Revolut or record a manual payment.",
        unavailableTitle: "Step not ready",
        unavailableGoBack: "Go back",
        unavailableConfirm: "Proceed anyway",
        unavailableSummary: "This step is always available when lines are selected.",
        unavailableCollect: "The summary email has not been sent for all selected lines yet. Proceed anyway?",
        unavailableSupplier: "The summary email has not been sent for all selected lines yet. Proceed anyway?",
        unavailableInvoice:
          "Earlier steps may not be complete for all selected lines (summary, collection, or received invoice). Proceed anyway?",
        unavailablePay:
          "Earlier steps may not be complete for all selected lines (summary, invoice, or collection). Proceed anyway?",
      },
      revolut: {
        bannerTitle: "Revolut payout in progress",
        status: "Status: {{status}}",
        referenceLabel: "Revolut payment reference",
        payButton: "Pay with Revolut",
        referenceRequired: "Enter a reference for this Revolut payout",
        initiated: "Revolut payout started",
        completedToast: "Revolut payout completed — lines updated",
        pendingReconciliationLabel:
          "Pending ledger reconciliation (funds moved in Revolut; automatic retries)",
        failedToast: "Revolut payout failed or cancelled",
        legalRequired: "Payee legal profile and IBAN must be complete",
        blockedActive: "A Revolut payout is already in progress for this payee",
        error: "Could not start Revolut payout",
      },
    },
  },
} as const;
